import { useState, useRef, useEffect } from "react";
import Box from "@mui/material/Box";
import { Stack, Typography, CardActions } from "@mui/material";
import Toast from "../../utils/Toast";
import LoadingCircle from "../../utils/LoadingCircle";
import { HttpMainApi } from "../../interface/main-api";
import { userState } from "../../interface/MainInterface";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import CardMedia from "@mui/material/CardMedia";
import "react-calendar/dist/Calendar.css";
import Button from "@mui/material/Button";
import CancelIcon from "@mui/icons-material/Cancel";
import { getFormattedDateTime } from "../../utils/getFormattedDateTime";
import { hpointAssets } from "../../design-system/hpoint/assets";
interface propsType {
  userState: userState;
}
interface ReservationDetails {
  userName: string;
  userId: string;
  inputDt: string;
  reservDt: string;
  reservOption: string;
  deviceNum: string;
}

interface Reservations {
  [key: string]: ReservationDetails;
}

interface ReservationInfo {
  sk: string;
  pk: string;
  reservations: Reservations;
}

interface Reservation {
  sk: string;
  pk: string;
  reservInfo: ReservationInfo;
  state: string;
}

const mainApi = new HttpMainApi();

const ReservationScreenEnd = (props: propsType) => {
  const toastRef: any = useRef();
  const [isLoading, setIsLoading] = useState(false);
  const [reservationData, setReservationData] = useState<Reservation[]>([]);
  const [noData, setNoData] = useState(true);
  const [lastCancel, setLastCancel] = useState<string[]>([]);

  useEffect(() => {
    get_my_reservation();
  }, []);

  const get_my_reservation = async () => {
    setIsLoading(true);
    const { formatpk, formathoursTime } = getFormattedDateTime();
    const userId = props.userState.userId;
    const pk = userId;
    const sk = formatpk;
    const param: any = {
      pk: pk,
      sk: sk,
      reservState: "신청",
      reservFilter: "SCREEN",
    };
    const res = await mainApi.get_month_reserv_data(param);
    if (res.code === "200") {
      setNoData(false);
      setReservationData(res.response.result as Reservation[]);
      const sortList: any[] = [];
      const sortedResult = res.response.result.map((item: any) => {
        sortList.push(...Object.keys(item.reservInfo.reservations));
        return sortList;
      });
      const filteredResult = sortList.filter((time) => time < formathoursTime);
      setLastCancel(filteredResult);
    } else if (res.code === "400") {
      setNoData(true);
    } else {
      alert(`알수 없는 에러가 발생했습니다.\n- CODE : ${res.code}\n- message : ${res.response.error_msg}`);
    }
    setIsLoading(false);
  };

  function sortReservations(reservations: { [key: string]: any }) {
    const sortedReservations: { [key: string]: any } = {};
    const keys = Object.keys(reservations).sort((a, b) => {
      // 시간을 Date 객체로 변환
      const timeA = new Date(`1970-01-01T${a}`);
      const timeB = new Date(`1970-01-01T${b}`);

      // Date 객체의 타임스탬프를 비교
      return timeA.getTime() - timeB.getTime();
    });

    for (const key of keys) {
      sortedReservations[key] = reservations[key];
    }

    return sortedReservations;
  }

  const reservation_cancel = async (pk: string, sk: string) => {
    const communityPrice = 0;
    const confirmCancel = window.confirm(`예약을 취소하시겠습니까?\n[사용료] ${communityPrice}원`);
    if (confirmCancel) {
      const { formattedDateTime, formatpk } = getFormattedDateTime();
      const param: any = {
        pk: pk,
        sk: sk,
        inputDt: formattedDateTime,
        communityPrice: communityPrice,
        CancelUpdateDt: formatpk,
        CancelUpdateId: props.userState.userId,
      };
      const res = await mainApi.put_cancel_my_reservation_price(param);
      if (res.code === "200") {
        alert("예약이 취소되었습니다.");
        get_my_reservation();
      } else if (res.code === "400") {
        alert("예약 취소에 실패하였습니다.\n다시 조회 후 진행해주세요.");
      } else {
        alert(`알수 없는 에러가 발생했습니다.\n- CODE : ${res.code}\n- message : ${res.response.error_msg}`);
      }
    }
  };
  const reservationCount = reservationData.reduce(
    (count, reservation) => count + Object.keys(reservation.reservInfo.reservations).length,
    0,
  );

  return (
    <>
      <Stack
        className="hpoint-renewal hp-reservation-page hp-screen-history-page"
        spacing={2}
        alignItems="center"
      >
        <Box component="header" className="hp-screen-history-header">
          <Box>
            <Typography component="h1" className="hp-page-title">
              스크린골프 예약 내역
            </Typography>
            <Typography className="hp-page-description">
              예약한 날짜와 타석을 확인하고 관리할 수 있습니다.
            </Typography>
          </Box>
          {!noData && <span className="hp-screen-history-header__count">총 {reservationCount}건</span>}
        </Box>

        {noData ? (
          <Typography className="hp-empty-state" variant="button" display="block">
            예약된 내용이 없습니다.
          </Typography>
        ) : (
          <Box className="hp-screen-history-list">
            {reservationData.flatMap((reservData) =>
              Object.entries(reservData.reservInfo.reservations).map(([time, details]) => {
                const isRoom = details.deviceNum === "ROOM";
                const deviceLabel = isRoom ? "스크린룸" : details.deviceNum;
                const cancelDisabled = lastCancel.includes(details.reservOption) && isRoom;

                return (
                  <Card
                    component="article"
                    aria-label={`${deviceLabel} ${details.reservOption} 예약`}
                    className="hp-card hp-screen-history-card"
                    key={`${reservData.pk}-${reservData.sk}-${time}`}
                  >
                    <Box className="hp-screen-history-card__visual">
                      <CardMedia
                        className="hp-screen-history-card__media"
                        component="img"
                        image={isRoom ? hpointAssets.golf.roomResult : hpointAssets.golf.screenResult}
                        alt={isRoom ? "스크린골프 룸" : "스크린골프 타석"}
                      />
                      <span className="hp-screen-history-card__status">예약 확정</span>
                    </Box>

                    <CardContent>
                      <Box className="hp-screen-history-card__heading">
                        <Box>
                          <Typography className="hp-screen-history-card__eyebrow">
                            {isRoom ? "프라이빗 스크린룸" : "스크린골프 타석"}
                          </Typography>
                          <Typography component="h2" className="hp-screen-history-card__title">
                            스크린골프 예약 완료
                          </Typography>
                        </Box>
                        <span className="hp-screen-history-card__device">{deviceLabel}</span>
                      </Box>

                      <Box className="hp-screen-history-card__details">
                        <Box className="hp-screen-history-card__detail hp-screen-history-card__detail--date">
                          <span>이용 날짜</span>
                          <strong>{details.reservDt}</strong>
                        </Box>
                        <Box className="hp-screen-history-card__detail">
                          <span>예약 시간</span>
                          <strong>{details.reservOption}</strong>
                        </Box>
                        <Box className="hp-screen-history-card__detail">
                          <span>이용 타석</span>
                          <strong>{deviceLabel}</strong>
                        </Box>
                      </Box>

                      <Typography className="hp-screen-history-card__created">
                        {details.inputDt}에 예약되었습니다.
                      </Typography>
                    </CardContent>

                    <CardActions>
                      <Button
                        className={cancelDisabled ? "hp-button hp-button--secondary" : "hp-button hp-button--danger"}
                        disabled={cancelDisabled}
                        variant="contained"
                        endIcon={<CancelIcon />}
                        onClick={() => reservation_cancel(reservData.pk, reservData.sk)}
                      >
                        <Typography variant="button">{cancelDisabled ? "취소 불가" : "예약취소"}</Typography>
                      </Button>
                    </CardActions>
                  </Card>
                );
              }),
            )}
          </Box>
        )}
      </Stack>

      <Toast ref={toastRef} />
      <LoadingCircle loading={isLoading} />
    </>
  );
};

export default ReservationScreenEnd;
